import { listCustomerSegments, listSegmentCustomerIds } from './segments';

// ─────────────────────────────────────────────────────────────────────────────
// Active Customer — a sales feature: businesses define what "active" means
// (currently: any invoice, paid or unpaid, in the last N days — this captures
// bookings, orders, and product sales since they all flow through invoices).
// Off by default; configured in the Customers app settings.
// ─────────────────────────────────────────────────────────────────────────────

export const ACTIVE_CUSTOMER_SETTING_EVENT = 'activeCustomerSettingChanged';
export const DEFAULT_ACTIVE_CUSTOMER_DAYS = 45;

export interface ActiveCustomerStatus {
  enabled: boolean;
  days: number;
  /** Customer ids with a qualifying invoice — empty set when disabled. */
  activeIds: Set<string>;
}

const DISABLED: ActiveCustomerStatus = { enabled: false, days: DEFAULT_ACTIVE_CUSTOMER_DAYS, activeIds: new Set() };

// Short-lived cache so badge lookups in list modals don't refetch per open.
const CACHE_TTL_MS = 60_000;
const cache = new Map<string, { at: number; promise: Promise<ActiveCustomerStatus> }>();

export function invalidateActiveCustomerStatus(businessId?: string) {
  if (businessId) cache.delete(businessId);
  else cache.clear();
}

async function fetchStatus(businessId: string): Promise<ActiveCustomerStatus> {
  const segments = await listCustomerSegments(businessId);
  const active = segments.find((segment) => segment.system_key === 'active_customers');
  if (!active?.visible) return DISABLED;

  const rule = active.rules.find((item) => item.kind === 'invoice_within_days');
  const days = Math.max(1, Number(rule?.value?.days) || DEFAULT_ACTIVE_CUSTOMER_DAYS);
  const ids = new Set<string>();
  let offset = 0;
  let total = Number.POSITIVE_INFINITY;
  while (offset < total && offset < 20000) {
    const page = await listSegmentCustomerIds(businessId, active.id, { limit: 5000, offset });
    total = page.total;
    for (const id of page.ids) ids.add(id);
    if (page.ids.length === 0) break;
    offset += page.ids.length;
  }

  return { enabled: true, days, activeIds: ids };
}

/** Resolve the active-customer config + the set of currently-active ids (cached ~60s). */
export function getActiveCustomerStatus(
  businessId: string,
  opts: { force?: boolean } = {},
): Promise<ActiveCustomerStatus> {
  const hit = cache.get(businessId);
  if (!opts.force && hit && Date.now() - hit.at < CACHE_TTL_MS) return hit.promise;

  const promise = fetchStatus(businessId).catch(err => {
    cache.delete(businessId);
    console.error('Failed to load active customer status:', err);
    return DISABLED;
  });
  cache.set(businessId, { at: Date.now(), promise });
  return promise;
}
